import { useQuery } from '@tanstack/react-query';
import {
  FilterTag,
  FilterDropdown,
  ComboboxSearch,
} from 'modules/shared-components/filters';
import { useMemo } from 'react';
import {
  ExchangeService,
  exchangeKeys,
} from 'api/services/httpServices/ExchangeService';
import { useDebouncedState } from 'utils/useDebounce';
import AssetIcon, {
  AssetIconStretch,
} from 'modules/shared-components/asset/asset-icon';

type PairTagProps = { value: number } & Omit<
  React.ComponentProps<typeof FilterTag>,
  'label'
>;

export function PairFilterTag({ value: id, ...rest }: PairTagProps) {
  const { data: pair } = useQuery({
    queryFn: () => ExchangeService.getPair(id),
    queryKey: exchangeKeys.getPair(id),
  });

  return pair ? (
    <FilterTag
      {...rest}
      icon={<AssetIconStretch imgName={pair.baseAsset} />}
      label={`${pair.baseAsset}/${pair.quoteAsset}`}
    />
  ) : null;
}

type PairFilterProps = {
  value: number[];
  onChange: (value: number[]) => void;
};

export function PairFilter({ value = [], onChange }: PairFilterProps) {
  const [query, debouncedQuery, setQuery] = useDebouncedState('', 250);
  const {
    data: pairs,
    isFetching,
    isError,
  } = useQuery({
    queryFn: () =>
      ExchangeService.getPairs({ symbol: debouncedQuery, limit: 10 }),
    queryKey: exchangeKeys.getPairs({ symbol: debouncedQuery, limit: 10 }),
    enabled: debouncedQuery.length >= 3,
    keepPreviousData: true,
  });

  const options = useMemo(() => {
    return pairs?.map((pair) => ({
      label: `${pair.baseAsset}/${pair.quoteAsset}`,
      value: pair.id,
      icon: <AssetIcon imgName={pair.baseAsset} />,
    }));
  }, [pairs]);

  return (
    <FilterDropdown
      value={value}
      options={options}
      label="Pair"
      onChange={(val) => {
        onChange(val);
        setQuery('');
      }}
      isError={isError}
      search={
        <ComboboxSearch
          query={query}
          setQuery={setQuery}
          isLoading={isFetching}
        />
      }
    />
  );
}
